import React from "react";
import Downshift from "downshift";
import { useMultipleSelection } from "downshift";
import useMask from "./useParcelInputMask";
import debounce from "lodash/debounce";
import MultiselectTest from "./MultiselectTest";

interface AutoCompleteProps {
  getOptions: (value: string) => Promise<any>;
  onSearch: (value: string) => void;
  setSearchValue: (value: string) => void;
  searchValue: string;
  placeholder?: string;
  searchLabel: string;
  onAddressSearch?: (magicKeys: string[]) => void;
}

interface Option {
  label: string;
  value: string;
}

const inputStyle = {
  minHeight: "22px",
};

const inputClasses =
  "bg-white text-sm px-3 py-[1px] rounded-xl w-full text-black";

export default function AutoComplete(props: AutoCompleteProps) {
  const [items, setItems] = React.useState<Option[]>([]);
  const [loading, setLoading] = React.useState(false);
  const { onInput, onKeyDown } = useMask();

  const placeholder = props.placeholder || "Search...";

  const loadOptions = async (value: string) => {
    if (value) {
      setLoading(true);
      const options = await props.getOptions(value);
      setItems(options as any as Option[]);
      setLoading(false);
    } else {
      setItems([]);
      props.setSearchValue("");
    }
  };

  const debouncedLoadOptions = debounce(loadOptions, 15);

  const onInputValueChange = (inputValue: string) => {
    if (inputValue) {
      setLoading(true);
      props.setSearchValue(inputValue);
      debouncedLoadOptions(inputValue);
    } else {
      setItems([]);
      props.setSearchValue("");
    }
  };

  return (
    <Downshift
      onChange={(selection) => {
        if (selection) {
          if (props.searchLabel === "Address" && props.onAddressSearch) {
            props.onAddressSearch([selection.value]);
          } else {
            props.onSearch(selection.value);
          }
        }
      }}
      itemToString={(item) => (item ? item.label : props.searchValue)}
      onInputValueChange={onInputValueChange}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        inputValue,
        selectedItem,
        isOpen,
        clearSelection,
      }) => {
        const inputProps = getInputProps();
        inputProps.value = props.searchValue;

        return (
          <div>
            {props.searchLabel === "Parcel" ? (
              <input
                style={inputStyle}
                placeholder={placeholder}
                className={inputClasses}
                {...inputProps}
                onInput={onInput}
                onKeyDown={onKeyDown}
              />
            ) : (
              <input
                style={inputStyle}
                placeholder={placeholder}
                className={inputClasses}
                {...inputProps}
              />
            )}

            <ul
              className={`absolute text-sm py-1 w-4/5 bg-white text-black rounded-md z-50 max-h-72 md:w-[420px] overflow-auto shadow-xl border border-gray-200 ${(!isOpen || props.searchValue === "") && "hidden"
                } ${loading && "opacity-50"}`}
              {...getMenuProps()}
            >
              {isOpen ? (
                <>
                  {items?.length > 0 &&
                    items
                      .filter(
                        (item) =>
                          !inputValue ||
                          item.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                      )
                      .map((item, index) => {
                        const matchIndex = item.label
                          .toLowerCase()
                          .indexOf(props.searchValue.toLowerCase());
                        const matchLength = props.searchValue.length;
                        const before = item.label.slice(0, matchIndex);
                        const match = item.label.slice(
                          matchIndex,
                          matchIndex + matchLength
                        );
                        const after = item.label.slice(
                          matchIndex + matchLength
                        );

                        return (
                          <li
                            className={`${selectedItem === item && "font-bold"
                              } text-left w-auto px-1 py-0.5 hover:bg-gray-600 hover:text-white hover:underline cursor-pointer`}
                            key={`${item.value}${index}`}
                            {...getItemProps({
                              item,
                              index,
                            })}
                          >
                            <span>{before}</span>
                            <b className="bg-yellow-200 text-black">{match}</b>
                            <span>{after}</span>
                          </li>
                        );
                      })}
                  {items?.length === 0 &&
                    !loading &&
                    props.searchValue !== "" ? (
                    <li className="text-left w-auto px-1 py-0.5">
                      No results found
                    </li>
                  ) : null}
                </>
              ) : null}
            </ul>
          </div>
        );
      }}
    </Downshift>
  );
}
